import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Checkbox,
  Grid,
  Paper,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ServicesService from "../services/ServicesService";
import { catalogProductToLineItem, formatMoney, parsePriceToMinor } from "../utils/quotationUtils";

const PRODUCT_IMAGE_HEIGHT = 140;

function getProductImageUrl(product) {
  if (!product) return null;
  if (product.image_url) return product.image_url;
  const images = product.images;
  if (!Array.isArray(images) || images.length === 0) return null;
  const first = images[0];
  if (!first) return null;
  if (typeof first === "object" && first.url) return first.url;
  if (typeof first === "string") {
    try {
      const parsed = JSON.parse(first);
      return parsed?.url || null;
    } catch {
      return null;
    }
  }
  return null;
}

function productKey(product) {
  return String(product.catalog_product_id || product.product_id || product.id || product.retailer_id || "");
}

function ProductImage({ imageUrl, name, size = "card" }) {
  const isThumb = size === "thumb";
  const height = isThumb ? 40 : PRODUCT_IMAGE_HEIGHT;
  const width = isThumb ? 40 : "100%";

  return (
    <Box
      sx={{
        width,
        height,
        flexShrink: 0,
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "grey.100",
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt={name || "Product"}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            display: "block",
          }}
        />
      ) : (
        <Typography
          variant={isThumb ? "caption" : "h5"}
          color="text.disabled"
          fontWeight={600}
        >
          {(name || "?").charAt(0).toUpperCase()}
        </Typography>
      )}
    </Box>
  );
}

function ProductCard({ product, selected, onToggle }) {
  const key = productKey(product);
  const imageUrl = getProductImageUrl(product);
  const priceMinor = parsePriceToMinor(product.price ?? product.sale_price);
  const name = product.name || product.title || key;

  return (
    <Paper
      variant="outlined"
      onClick={onToggle}
      sx={{
        p: 1.5,
        height: "100%",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        borderColor: selected ? "primary.main" : "divider",
        borderWidth: selected ? 2 : 1,
        bgcolor: selected ? "action.selected" : "background.paper",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          borderColor: "primary.light",
          boxShadow: 1,
        },
      }}
    >
      <Box sx={{ position: "relative", mb: 1 }}>
        <Checkbox
          checked={selected}
          size="small"
          sx={{
            position: "absolute",
            top: 6,
            left: 6,
            zIndex: 1,
            p: 0.25,
            bgcolor: "rgba(255,255,255,0.92)",
            borderRadius: 0.5,
            boxShadow: 1,
          }}
          onClick={(e) => e.stopPropagation()}
          onChange={onToggle}
        />
        <ProductImage imageUrl={imageUrl} name={name} />
      </Box>

      <Typography
        variant="body2"
        fontWeight={600}
        title={name}
        sx={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          minHeight: 40,
          lineHeight: 1.25,
        }}
      >
        {name}
      </Typography>

      <Chip
        label="No Category"
        size="small"
        variant="outlined"
        sx={{ alignSelf: "flex-start", mt: 0.75, mb: 0.75, height: 22, fontSize: "0.7rem" }}
      />

      <Typography variant="body2" color="primary" fontWeight={600} sx={{ mt: "auto" }}>
        {formatMoney(priceMinor)}
        {" "}
        <Typography component="span" variant="caption" color="text.secondary">
          per pc
        </Typography>
      </Typography>
    </Paper>
  );
}

export default function CatalogProductPickerDialog({
  open,
  onClose,
  onConfirm,
  initialItems = [],
}) {
  const [loading, setLoading] = useState(false);
  const [catalogId, setCatalogId] = useState("");
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedMap, setSelectedMap] = useState({});

  useEffect(() => {
    if (!open) return;
    const map = {};
    initialItems.forEach((item) => {
      const key = item.retailer_id || item.catalog_product_id;
      if (key) map[key] = item;
    });
    setSelectedMap(map);
    setSearch("");

    (async () => {
      setLoading(true);
      try {
        const id = await ServicesService.getCatalog();
        setCatalogId(id || "");
        if (id) {
          const products = await ServicesService.getCatalogProducts(id);
          setCatalogProducts(Array.isArray(products) ? products : []);
        } else {
          setCatalogProducts([]);
        }
      } catch {
        setCatalogId("");
        setCatalogProducts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, initialItems]);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalogProducts;
    return catalogProducts.filter((p) => {
      const pid = productKey(p);
      const name = String(p.name || p.title || "");
      const retailer = String(p.retailer_id || "");
      return pid.toLowerCase().includes(q) || name.toLowerCase().includes(q) || retailer.toLowerCase().includes(q);
    });
  }, [catalogProducts, search]);

  const selectedItems = useMemo(
    () => Object.values(selectedMap),
    [selectedMap],
  );

  const selectedCount = selectedItems.length;
  const selectedQty = selectedItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const saleValueMinor = selectedItems.reduce((sum, item) => sum + (item.line_total_minor || 0), 0);

  const toggleProduct = (product) => {
    const key = productKey(product);
    if (!key) return;
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = catalogProductToLineItem(product, 1);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm?.(selectedItems);
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Select Products
        <Button onClick={onClose} color="inherit">Close</Button>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {!catalogId && !loading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Connect your WhatsApp catalog in Settings before adding products to a quotation.
          </Alert>
        )}
        <TextField
          fullWidth
          size="small"
          placeholder="Search products by name or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                minHeight: 400,
                maxHeight: 460,
                overflow: "auto",
                bgcolor: "grey.50",
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : filteredProducts.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                  No products found.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, minmax(0, 1fr))",
                      sm: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: 2,
                    alignItems: "stretch",
                  }}
                >
                  {filteredProducts.map((product) => {
                    const key = productKey(product);
                    return (
                      <ProductCard
                        key={key}
                        product={product}
                        selected={!!selectedMap[key]}
                        onToggle={() => toggleProduct(product)}
                      />
                    );
                  })}
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                minHeight: 400,
                maxHeight: 460,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {selectedCount === 0 ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "text.secondary",
                    textAlign: "center",
                    px: 2,
                  }}
                >
                  <ShoppingCartOutlinedIcon sx={{ fontSize: 48, mb: 1, color: "primary.light" }} />
                  <Typography variant="body2">No products selected yet</Typography>
                  <Typography variant="caption">Click on products to select them</Typography>
                </Box>
              ) : (
                <Box sx={{ overflow: "auto", flex: 1 }}>
                  {selectedItems.map((item) => (
                    <Box
                      key={item.retailer_id || item.catalog_product_id}
                      sx={{
                        display: "flex",
                        gap: 1.25,
                        mb: 1.5,
                        alignItems: "center",
                      }}
                    >
                      <ProductImage imageUrl={item.image_url} name={item.name} size="thumb" />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" noWrap fontWeight={600}>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Qty
                          {" "}
                          {item.quantity}
                          {" "}
                          •
                          {" "}
                          {formatMoney(item.line_total_minor)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
        <Box>
          <Typography variant="body2">
            Selected Items:
            {" "}
            {selectedCount}
            {" "}
            products (
            {selectedQty}
            {" "}
            items)
          </Typography>
          <Typography variant="body2" color="primary">
            Sale Value:
            {" "}
            {formatMoney(saleValueMinor)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={selectedCount === 0}>
            Add Products
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
